import { cn } from '@/utils/cn'
import type { FinanceRequestRow } from '@/services/financeService'

export type FinanceRequestClientKind = 'cliente' | 'assunto' | 'interno'

export type FinanceRequestClientDisplay = {
  label: string
  kind: FinanceRequestClientKind
  hint: string | null
}

const SUBJECT_FORM_KEYS = [
  'nomeCompleto',
  'razaoSocial',
  'nomeFantasia',
  'nome',
  'resumo',
  'empresa',
  'nomeEmpresa',
  'parceiro',
] as const

function pickString(formData: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = formData[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function resolveClientDisplayFallback(request: FinanceRequestRow): FinanceRequestClientDisplay {
  const empresa = request.project?.empresa
  if (empresa?.nomeFantasia?.trim()) {
    return { label: empresa.nomeFantasia.trim(), kind: 'cliente', hint: null }
  }
  if (empresa?.nome?.trim()) {
    return { label: empresa.nome.trim(), kind: 'cliente', hint: null }
  }
  if (request.project?.name?.trim()) {
    return { label: request.project.name.trim(), kind: 'cliente', hint: null }
  }

  const internalAreas = new Set(['GESTAO', 'PJ', 'SUPORTE', 'OUTROS'])
  if (internalAreas.has(request.area)) {
    return { label: 'Chamado interno', kind: 'interno', hint: null }
  }

  return { label: '—', kind: 'interno', hint: null }
}

export function getFinanceRequestClientDisplay(
  request: FinanceRequestRow,
): FinanceRequestClientDisplay {
  if (request.clientDisplay) return request.clientDisplay
  return resolveClientDisplayFallback(request)
}

export function getFinanceRequestClientName(request: FinanceRequestRow): string {
  return getFinanceRequestClientDisplay(request).label
}

/** Chamado respondido pelo solicitante e aguardando retorno do financeiro. */
export function isFinanceQueueAwaitingFinance(status: string): boolean {
  return status === 'RESPONDIDO_SOLICITANTE'
}

export function financeQueueRowClass(status: string): string {
  const base =
    'cursor-pointer border-b border-white/[0.06] transition-colors hover:bg-white/[0.02]'

  if (!isFinanceQueueAwaitingFinance(status)) return base

  return cn(
    base,
    'border-l-2 border-l-accent bg-accent/[0.07] hover:bg-accent/[0.12] ring-1 ring-inset ring-accent/10',
  )
}

export function pickFinanceRequestSubjectFromForm(
  formData: Record<string, unknown> | undefined,
): string | null {
  if (!formData) return null
  return pickString(formData, SUBJECT_FORM_KEYS)
}
